// src/components/MerchSection.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { mergeSiteContent } from "../utils/helpers";
import { typographyFor } from "../lib/typography";
import MediaRenderer from "./MediaRenderer";

function SmartLink({ href, children, className }) {
  if (/^https?:\/\//i.test(href || "")) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {children}
      </a>
    );
  }
  return <Link to={href || "/latdior#collection"} className={className}>{children}</Link>;
}

export default function MerchSection() {
  const [site, setSite] = useState(() => mergeSiteContent());

  useEffect(() => {
    const off = onSnapshot(
      doc(db, "admin", "content"),
      (snap) => setSite(mergeSiteContent(snap.exists() ? snap.data() : {})),
      () => setSite(mergeSiteContent())
    );
    return () => off();
  }, []);

  const preview = site.pages.home.latdiorPreview || {};
  const merch = useMemo(() => {
    const homeMerch = Array.isArray(site.merch) ? site.merch.filter(Boolean) : [];
    const pageProducts = Array.isArray(site.pages.latdior.products)
      ? site.pages.latdior.products.filter(Boolean)
      : [];
    return homeMerch.length ? homeMerch : pageProducts;
  }, [site.merch, site.pages.latdior.products]);

  if (!merch.length) return null;

  return (
    <section className="overflow-hidden bg-gradient-to-r from-[#1a1a1a] via-[#111111] to-[#1a1a1a] px-6 py-20 text-white md:px-24 md:py-24">
      <div className="mb-10 flex flex-col justify-between gap-5 md:flex-row md:items-end">
        <div className="max-w-3xl">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-red-500">
            {preview.eyebrow || "Latdior"}
          </p>
          <h2 className="text-3xl font-extrabold uppercase md:text-5xl" style={typographyFor(site, "homeLatdiorTitle")}>
            {preview.title || "Collection Latdior"}
          </h2>
          {preview.description && (
            <p className="readable-copy mt-4 text-gray-300" style={typographyFor(site, "homeLatdiorText")}>
              {preview.description}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          <SmartLink
            href={preview.buttonUrl || "/latdior#collection"}
            className="rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black transition hover:bg-red-600 hover:text-white"
          >
            {preview.buttonLabel || "Découvrir Latdior"}
          </SmartLink>
          {preview.secondaryUrl && (
            <SmartLink
              href={preview.secondaryUrl}
              className="rounded-full border border-white/20 px-5 py-3 text-sm font-bold uppercase text-white transition hover:border-white hover:bg-white hover:text-black"
            >
              {preview.secondaryLabel || "Site officiel"}
            </SmartLink>
          )}
        </div>
      </div>
      <div className="max-w-full overflow-x-auto scrollbar-hide">
        <div className="flex gap-6 w-max px-2">
          {merch.map((img, i) => (
            <SmartLink
              key={i}
              href={site.merchLinks?.[i] || preview.buttonUrl || "/latdior#collection"}
              className="flex min-w-[300px] flex-col items-center overflow-hidden rounded-xl bg-neutral-800 shadow-lg transition-transform hover:scale-[1.025] md:min-w-[350px]"
            >
              <MediaRenderer
                src={img}
                type="auto"
                alt={`Latdior ${i + 1}`}
                background
                className="w-full h-[450px] object-cover"
              />
              <div className="p-4 w-full flex justify-center">
                <span className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-black transition hover:bg-gray-300" style={typographyFor(site, "homeLatdiorButton")}>
                  {site.merchLabels?.[i] || preview.cardLabel || "Voir / commander"}
                </span>
              </div>
            </SmartLink>
          ))}
        </div>
      </div>
    </section>
  );
}
